import React, { useEffect, useMemo, useState, useCallback } from 'react';
import LoadingSpinner from '../components/LoadingSpinner';
import Modal from '../components/Modal';
import { apiService } from '../services/apiService';
import { useToast } from '../components/Toast';
import { useAuth } from '../services/authContext';
import SetupGuard from '../components/SetupGuard';

const emptyForm  = { name: '', code: '', description: '', classId: '', teacherId: '' };
const emptyBatch = { name: '', year: new Date().getFullYear(), semester: '', teacherId: '', maxStudents: 30, status: 'active', startDate: '', endDate: '' };

const Subjects = () => {
  const { user }                          = useAuth();
  const { toast }                         = useToast();
  const isAdmin                           = user?.role === 'admin';
  const [modules, setModules]             = useState([]);
  const [classes, setClasses]             = useState([]);
  const [teachers, setTeachers]           = useState([]);
  const [programmes, setProgrammes]       = useState([]);
  const [assignments, setAssignments]     = useState({});
  const [batches, setBatches]             = useState([]);
  const [loading, setLoading]             = useState(true);
  const [saving, setSaving]               = useState(false);
  const [search, setSearch]               = useState('');
  const [filterProgramme, setFilterProgramme] = useState('');
  const [isModalOpen, setModal]           = useState(false);
  const [current, setCurrent]             = useState(null);
  const [form, setForm]                   = useState(emptyForm);
  // Batch management
  const [batchPanel, setBatchPanel]       = useState(null);
  const [batchForm, setBatchForm]         = useState(emptyBatch);
  const [editingBatch, setEditingBatch]   = useState(null);
  const [batchSaving, setBatchSaving]     = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [moduleData, classData, teacherData, progData] = await Promise.all([
        apiService.getAll('modules'),
        apiService.getAll('classes'),
        apiService.getAll('teachers'),
        apiService.getAll('programmes-list'),
      ]);

      const classList = classData || [];
      const assignmentEntries = await Promise.all(
        classList.map(async item => {
          const cs = await apiService.get(`modules/class/${item._id}`);
          return (cs || []).map(s => ({
            moduleId: s._id, classId: item._id,
            className: item.name, programme: item.programmeName || item.programme || '',
            teacherId: s.teacherId || '', teacherName: s.teacherName || 'Not assigned',
            semester: s.semester || '', studentCount: s.studentCount || 0
          }));
        })
      );

      const assignmentMap = assignmentEntries.flat().reduce((acc, item) => {
        if (!acc[item.moduleId]) acc[item.moduleId] = [];
        acc[item.moduleId].push(item);
        return acc;
      }, {});

      setModules(moduleData || []);
      setClasses(classList);
      setTeachers(teacherData || []);
      setProgrammes(progData || []);
      setAssignments(assignmentMap);
    } catch (err) {
      toast(err.response?.data?.message || 'Failed to load modules.', 'error');
    } finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { loadData(); }, [loadData]);

  // Group classes by programme
  const classesByProgramme = useMemo(() =>
    classes.reduce((acc, c) => {
      const programme = c.programmeName || c.programme || 'Other';
      if (!acc[programme]) acc[programme] = [];
      acc[programme].push(c);
      return acc;
    }, {}), [classes]);

  const programmeOptions = useMemo(() =>
    Object.keys(classesByProgramme).sort((a, b) => a.localeCompare(b)),
    [classesByProgramme]);

  const filtered = useMemo(() =>
    modules.filter(m => {
      const sa = assignments[m._id] || [];
      const text = [m.name, m.code, m.description, ...sa.map(a => a.className), ...sa.map(a => a.programme)].join(' ').toLowerCase();
      const matchSearch = text.includes(search.toLowerCase());
      const matchProg   = filterProgramme ? sa.some(a => a.programme === filterProgramme) : true;
      return matchSearch && matchProg;
    }), [modules, assignments, search, filterProgramme]);

  const handleChange = e => setForm(p => ({ ...p, [e.target.name]: e.target.value }));

  const openAdd = () => {
    setCurrent(null);
    let initialTeacherId = '';
    if (user?.role === 'teacher') {
      const currentTeacher = teachers.find(t => t.userId === user.id);
      if (currentTeacher) initialTeacherId = currentTeacher._id;
    }
    setForm({ ...emptyForm, teacherId: initialTeacherId });
    setModal(true);
  };

  const openEdit = s => {
    const first = assignments[s._id]?.[0];
    setCurrent(s);
    let initialTeacherId = first?.teacherId || '';
    if (!initialTeacherId && user?.role === 'teacher') {
      const currentTeacher = teachers.find(t => t.userId === user.id);
      if (currentTeacher) initialTeacherId = currentTeacher._id;
    }
    setForm({ name: s.name, code: s.code, description: s.description || '', classId: first?.classId || '', teacherId: initialTeacherId });
    setModal(true);
  };

  const handleSubmit = async e => {
    e.preventDefault();
    try {
      setSaving(true);
      let moduleId = current?._id;
      if (current) {
        await apiService.update('modules', moduleId, { name: form.name, code: form.code, description: form.description });
        toast('Module updated.', 'success');
      } else {
        const saved = await apiService.create('modules', { name: form.name, code: form.code, description: form.description });
        moduleId = saved._id;
        toast('Module created.', 'success');
      }

      // Remove old assignments not matching new class
      const prev = current ? assignments[current._id] || [] : [];
      await Promise.all(
        prev.filter(a => !form.classId || String(a.classId) !== String(form.classId))
            .map(a => apiService.raw.delete(`modules/class/${a.classId}/${moduleId}`))
      );

      if (form.classId) {
        await apiService.post('modules/class', {
          classId: Number(form.classId),
          moduleId: Number(moduleId),
          teacherId: form.teacherId ? Number(form.teacherId) : null,
        });
      }

      setModal(false); setCurrent(null); setForm(emptyForm);
      await loadData();
    } catch (err) {
      toast(err.response?.data?.message || 'Failed to save module.', 'error');
    } finally { setSaving(false); }
  };

  const handleDelete = async id => {
    if (!window.confirm('Delete this module?')) return;
    try {
      await apiService.delete('modules', id);
      toast('Module deleted.', 'success');
      await loadData();
    } catch (err) { toast(err.response?.data?.message || 'Failed to delete.', 'error'); }
  };

  // No longer used, handled by Classes.js
  // const openBatches = m => { ... };

  const handleBatchSubmit = async e => {
    e.preventDefault();
    try {
      setBatchSaving(true);
      const payload = { ...batchForm, moduleId: batchPanel.module._id, classId: batchForm.classId || assignments[batchPanel.module._id]?.[0]?.classId };
      if (editingBatch) {
        await apiService.update('batches', editingBatch.id, payload);
        toast('Batch updated.', 'success');
      } else {
        await apiService.create('batches', payload);
        toast('Batch created.', 'success');
      }
      setEditingBatch(null);
      setBatchForm({ ...emptyBatch, year: new Date().getFullYear() });
      await loadData();
      const updated = (await apiService.getAll('batches')).filter(b => b.subjectId === batchPanel.module._id);
      setBatchPanel(p => ({ ...p, list: updated }));
    } catch (err) { toast(err.response?.data?.message || 'Failed to save batch.', 'error'); }
    finally { setBatchSaving(false); }
  };

  const handleDeleteBatch = async id => {
    if (!window.confirm('Delete this batch?')) return;
    try {
      await apiService.delete('batches', id);
      toast('Batch deleted.', 'success');
      const updated = batches.filter(b => b.id !== id && b.subjectId === batchPanel.module._id);
      setBatchPanel(p => ({ ...p, list: updated }));
      setBatches(prev => prev.filter(b => b.id !== id));
    } catch (err) { toast(err.response?.data?.message || 'Failed to delete batch.', 'error'); }
  };

  const tableContent = (
    <div className="table-responsive">
      <table className="data-table">
        <thead>
          {isAdmin ? (
            <tr><th>Code</th><th>Module</th><th>Programme</th><th>Assigned Classes</th><th>Lecturer</th><th>Actions</th></tr>
          ) : (
            <tr><th>Code</th><th>Module</th><th>Assigned Class</th><th>Semester</th><th>Students</th><th>Actions</th></tr>
          )}
        </thead>
        <tbody>
          {filtered.length === 0 ? (
            <tr><td colSpan={isAdmin ? 6 : 6} className="no-data-message">No modules found.</td></tr>
          ) : filtered.map(m => {
            const sa = assignments[m._id] || [];

            if (!isAdmin) {
              // Simplified Lecturer View
              return (
                <tr key={m._id}>
                  <td><strong>{m.code}</strong></td>
                  <td>
                    <div style={{ fontWeight: 600 }}>{m.name}</div>
                    {m.description && <div style={{ fontSize: '0.75rem', color: 'var(--text-light)', marginTop: '2px' }}>{m.description}</div>}
                  </td>
                  <td>{sa.map(a => a.className).join(', ') || 'Not assigned'}</td>
                  <td>{sa.map(a => a.semester).filter(Boolean).join(', ') || '—'}</td>
                  <td>
                    <div style={{ fontWeight: 600 }}>
                      {sa.reduce((sum, a) => sum + (a.studentCount || 0), 0)} students
                    </div>
                  </td>
                  <td>
                    <div className="action-buttons" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                      <button className="btn-icon" style={{ color: '#1976D2', fontWeight: 500 }} onClick={() => window.location.href=`/attendance?classId=${sa[0]?.classId}`}>Attendance</button>
                      <button className="btn-icon" style={{ color: '#388E3C', fontWeight: 500 }} onClick={() => window.location.href=`/materials?moduleId=${m._id}`}>Materials</button>
                      <button className="btn-icon" style={{ color: '#F57C00', fontWeight: 500 }} onClick={() => window.location.href=`/assessments?moduleId=${m._id}`}>Assessments</button>
                      <button className="btn-icon" style={{ color: '#7B1FA2', fontWeight: 500 }} onClick={() => window.location.href=`/grades?moduleId=${m._id}`}>Grades</button>
                      <button className="btn-icon" onClick={() => openEdit(m)}>Edit</button>
                    </div>
                  </td>
                </tr>
              );
            }

            return (
              <tr key={m._id}>
                <td><strong>{m.code}</strong></td>
                <td>
                  <div style={{ fontWeight: 600 }}>{m.name}</div>
                  {m.description && <div style={{ fontSize: '0.75rem', color: 'var(--text-light)', marginTop: '2px' }}>{m.description}</div>}
                </td>
                <td>{[...new Set(sa.map(a => a.programme).filter(Boolean))].join(', ') || '—'}</td>
                <td>{sa.map(a => a.className).join(', ') || 'Not assigned'}</td>
                <td>{sa.map(a => a.teacherName).join(', ') || 'Not assigned'}</td>
                <td>
                  <div className="action-buttons">
                    <button className="btn-icon" onClick={() => openEdit(m)}>Edit</button>
                    <button className="btn-icon" style={{ color: '#c62828' }} onClick={() => handleDelete(m._id)}>Delete</button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  const statusColor = s => ({ active: '#388E3C', completed: '#1976D2', cancelled: '#c62828' }[s] || '#666');

  if (loading) return <LoadingSpinner message="Loading modules..." />;

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Modules</h1>
        <button className="btn btn-primary" onClick={openAdd}>+ Add Module</button>
      </div>

      {isAdmin ? (
        <SetupGuard checks={[
          {
            ok: programmes.length > 0 || classes.length > 0,
            icon: '🏛️',
            label: 'No Programmes exist yet',
            description: 'Create a Programme first so you have classes to assign modules to.',
            action: { label: 'Create Programme', to: '/programmes' },
          },
          {
            ok: classes.length > 0,
            icon: '🏫',
            label: 'No Classes / Batches exist yet',
            description: 'Add a class or batch under a programme before creating modules.',
            action: { label: 'Create Class', to: '/programmes' },
          },
          {
            ok: teachers.length > 0,
            icon: '👨‍🏫',
            label: 'No Lecturers exist yet',
            description: 'Add at least one lecturer so you can assign them to modules.',
            action: { label: 'Add Lecturer', to: '/lecturers' },
          },
        ]}>
          <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
            <input style={{ flex: 1, minWidth: 200 }} type="text" placeholder="Search modules..." value={search} onChange={e => setSearch(e.target.value)} />
            <select value={filterProgramme} onChange={e => setFilterProgramme(e.target.value)} style={{ minWidth: 200 }}>
              <option value="">All Programmes</option>
              {programmeOptions.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          {tableContent}
        </SetupGuard>
      ) : (
        <>
          <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
            <input style={{ flex: 1, minWidth: 200 }} type="text" placeholder="Search modules..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          {tableContent}
        </>
      )}

      {/* Add/Edit Module Modal */}
      <Modal isOpen={isModalOpen} onClose={() => setModal(false)} title={current ? 'Edit Module' : 'Add Module'}>
        <form onSubmit={handleSubmit} className="form">
          <div className="form-row">
            <div className="form-group">
              <label>Module Name *</label>
              <input name="name" value={form.name} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label>Module Code *</label>
              <input name="code" value={form.code} onChange={handleChange} required />
            </div>
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea name="description" value={form.description} onChange={handleChange} rows={3} />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Assign to Class</label>
              <select name="classId" value={form.classId} onChange={handleChange} required>
                <option value="">Select Class</option>
                {programmeOptions.length > 0 ? (
                  programmeOptions.map(prog => (
                    <optgroup key={prog} label={prog}>
                      {(classesByProgramme[prog] || []).map(c => (
                        <option key={c._id} value={c._id}>{c.name}</option>
                      ))}
                    </optgroup>
                  ))
                ) : (
                  classes.map(c => <option key={c._id} value={c._id}>{c.name}</option>)
                )}
              </select>
            </div>
            {user?.role === 'admin' && (
              <div className="form-group">
                <label>Assign Lecturer</label>
                <select name="teacherId" value={form.teacherId} onChange={handleChange} required>
                  <option value="">Select Lecturer</option>
                  {teachers.map(t => <option key={t._id} value={t._id}>{t.firstName} {t.lastName}</option>)}
                </select>
              </div>
            )}
          </div>
          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setModal(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving...' : current ? 'Update Module' : 'Add Module'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Batches Modal */}
      <Modal isOpen={!!batchPanel} onClose={() => setBatchPanel(null)} title={`Batches — ${batchPanel?.module?.name}`}>
        {batchPanel && (
          <div>
            {/* Existing batches */}
            {batchPanel.list.length === 0 ? (
              <div className="no-data-message" style={{ marginBottom: '1.5rem' }}>No batches yet.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', marginBottom: '1.5rem' }}>
                {batchPanel.list.map(b => (
                  <div key={b.id} style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: 8, padding: '0.85rem 1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
                    <div>
                      <strong>{b.name}</strong>
                      <span style={{ marginLeft: '0.5rem', background: `${statusColor(b.status)}22`, color: statusColor(b.status), padding: '0.1rem 0.4rem', borderRadius: 4, fontSize: '0.78rem', fontWeight: 600 }}>{b.status}</span>
                      <div style={{ fontSize: '0.82rem', color: 'var(--text-light)', marginTop: '0.2rem' }}>
                        {b.year}{b.semester && ` · ${b.semester}`} · Max: {b.maxStudents} students
                        {b.teacherName && ` · ${b.teacherName}`}
                        {b.startDate && ` · ${new Date(b.startDate).toLocaleDateString()}`}
                        {b.endDate && ` – ${new Date(b.endDate).toLocaleDateString()}`}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '0.4rem' }}>
                      <button className="btn-icon" onClick={() => { setEditingBatch(b); setBatchForm({ name: b.name, year: b.year, semester: b.semester || '', teacherId: b.teacherId || '', maxStudents: b.maxStudents, status: b.status, startDate: b.startDate || '', endDate: b.endDate || '', classId: b.classId }); }}>Edit</button>
                      <button className="btn-icon" style={{ color: '#c62828' }} onClick={() => handleDeleteBatch(b.id)}>Delete</button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Add/Edit batch form */}
            <h3 style={{ margin: '0 0 0.75rem', fontSize: '0.95rem' }}>{editingBatch ? 'Edit Batch' : 'Add New Batch'}</h3>
            <form onSubmit={handleBatchSubmit} className="form">
              <div className="form-row">
                <div className="form-group">
                  <label>Batch Name *</label>
                  <input value={batchForm.name} onChange={e => setBatchForm(p => ({ ...p, name: e.target.value }))} required placeholder="e.g. Batch A, Morning Group" />
                </div>
                <div className="form-group">
                  <label>Year *</label>
                  <input type="number" value={batchForm.year} onChange={e => setBatchForm(p => ({ ...p, year: e.target.value }))} required min={2020} max={2040} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Semester</label>
                  <select value={batchForm.semester} onChange={e => setBatchForm(p => ({ ...p, semester: e.target.value }))}>
                    <option value="">Select</option>
                    <option value="Semester 1">Semester 1</option>
                    <option value="Semester 2">Semester 2</option>
                    <option value="Full Year">Full Year</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Max Students</label>
                  <input type="number" value={batchForm.maxStudents} onChange={e => setBatchForm(p => ({ ...p, maxStudents: e.target.value }))} min={1} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Assign Lecturer</label>
                  <select value={batchForm.teacherId} onChange={e => setBatchForm(p => ({ ...p, teacherId: e.target.value }))}>
                    <option value="">Select Lecturer</option>
                    {teachers.map(t => <option key={t._id} value={t._id}>{t.firstName} {t.lastName}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select value={batchForm.status} onChange={e => setBatchForm(p => ({ ...p, status: e.target.value }))}>
                    <option value="active">Active</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Start Date</label>
                  <input type="date" value={batchForm.startDate} onChange={e => setBatchForm(p => ({ ...p, startDate: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>End Date</label>
                  <input type="date" value={batchForm.endDate} onChange={e => setBatchForm(p => ({ ...p, endDate: e.target.value }))} />
                </div>
              </div>
              <div className="form-actions">
                {editingBatch && <button type="button" className="btn btn-secondary" onClick={() => { setEditingBatch(null); setBatchForm({ ...emptyBatch, year: new Date().getFullYear() }); }}>Cancel Edit</button>}
                <button type="submit" className="btn btn-primary" disabled={batchSaving}>
                  {batchSaving ? 'Saving...' : editingBatch ? 'Update Batch' : 'Add Batch'}
                </button>
              </div>
            </form>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Subjects;
